import { forwardRef } from "react";
import { useTheme } from "@mui/material/styles";
import { Button as MuiButton, Link as MuiLink, CircularProgress, Tooltip } from "@mui/material";

/**
 * Wraps the MUI button to apply our theme.
 * @param {object} props
 * @param {"button" | "reset" | "submit"} props.type The type of button. Defaults to `button`.
 * @param {"xs" | "sm" | "md" | "lg" | "xl" | "2xl" | { fontSize: string; paddingX: string; paddingY: string }} props.size The size of the button.
 * @param {"primary" | "secondary" | "danger" | { buttonBackgroundColor: string; buttonTextColor: string; }} props.colorTheme The color theme for the button. Defaults to `primary`. This prop is ignored if the `themeObj` prop is provided.
 * @param {boolean} props.loading `true` to display a loader instead of the button content.
 * @param {boolean} props.disabled `true` to disable the button. Defaults to `false`.
 * @param {string} props.href Link for the button. An `<a>` tag is rendered instead of a `<button>` tag when this prop is provided.
 * @param {{ text: string; show?: boolean; } | undefined} props.tooltipProps Props for the tooltip. To show the tooltip on mouseover, leave the `show` property undefined.
 * @param {React.MouseEventHandler<HTMLButtonElement> | undefined} props.handleOnClick Handler for the button click event.
 * @param {React.ReactNode} props.iconComponent The icon component.
 * @param {React.ReactNode} props.children Content for the button.
 */
function Button(
    {
        type = "button",
        size,
        colorTheme = "primary",
        handleOnClick,
        loading = false,
        disabled = false,
        forceRounded = false,
        href,
        tooltipProps,
        iconComponent,
        children,
        ...props // Props spread and ref are needed so this component can work with the MUI tooltip component
    },
    ref,
) {
    const theme = useTheme();

    let paddingX = null;
    let paddingY = null;
    let fontSize = null;
    if (typeof size === "object") {
        // Size has been overridden by passing in an object for the size prop
        fontSize = size.fontSize;
        paddingX = size.paddingX;
        paddingY = size.paddingY;
    } else if (!!iconComponent && !children) {
        // We need to use different padding if there's no size override
        // and it's an button with only an icon and no content
        fontSize = undefined; // No font size for icon only button
        paddingX = theme.buttonSizes[size].iconPaddingX;
        paddingY = theme.buttonSizes[size].iconPaddingY;
    } else {
        fontSize = theme.buttonSizes[size].fontSize;
        paddingX = theme.buttonSizes[size].paddingX;
        paddingY = theme.buttonSizes[size].paddingY;
    }

    // Need to support using the theme stored in the database if it's a survey button
    const isColorThemeOverridden = typeof colorTheme === "object"; // Color theme is overridden if an object is passed in
    let backgroundColor = null;
    let backgroundHover = null;
    let backgroundFocus = null;
    let backgroundActive = null;
    let borderColor = null;
    let color = null;
    let rounded = true;
    if (isColorThemeOverridden) {
        backgroundColor = colorTheme.buttonBackgroundColor;
        backgroundHover = colorTheme.buttonBackgroundColor;
        backgroundFocus = colorTheme.buttonBackgroundColor;
        backgroundActive = colorTheme.buttonBackgroundColor;
        color = colorTheme.buttonTextColor;
        rounded = colorTheme.roundedButtons;
    } else {
        backgroundColor = theme.buttonPalette[colorTheme].background;
        backgroundHover = theme.buttonPalette[colorTheme].backgroundHover;
        backgroundFocus = theme.buttonPalette[colorTheme].backgroundFocus;
        backgroundActive = theme.buttonPalette[colorTheme].backgroundActive;
        borderColor = theme.buttonPalette[colorTheme].borderColor;
        color = theme.buttonPalette[colorTheme].text;
    }
    // Workaround that allows rounding buttons without overriding the color theme.
    // Specifically, this was added to allow making secondary buttons rounded.
    if (forceRounded) {
        rounded = true;
    }

    const content = (
        <>
            {loading && (
                <CircularProgress
                    sx={{
                        position: "absolute",
                        color,
                    }}
                    size={25}
                />
            )}
            <span>
                {iconComponent}
                {children}
            </span>
        </>
    );

    const componentProps = {
        ...props,
        ref,
        type,
        disabled,
        sx: {
            width: "fit-content", // Button fits content to allow padding to define the button
            minWidth: "0px", // Removes spacing around the content
            borderRadius: rounded ? "6px" : "0px",
            textTransform: "none",
            fontSize,
            paddingX,
            paddingY,
            backgroundColor,
            borderColor,
            color,
            borderStyle: isColorThemeOverridden || colorTheme === "primary" ? "none" : "solid", // Only the secondary color theme has a border
            borderWidth: "1px",
            "&:hover": {
                backgroundColor: backgroundHover,
            },
            "&:focus": {
                backgroundColor: backgroundFocus,
            },
            "&:active": {
                backgroundColor: backgroundActive,
            },
            "&:disabled": {
                backgroundColor: theme.buttonPalette.disabled.background,
                borderColor: theme.buttonPalette.disabled.background,
                color: theme.buttonPalette.disabled.text,
            },
        },
        onClick: handleOnClick,
    };

    const component = (
        <>
            {href ? (
                <MuiLink {...componentProps} underline="none" href={href} rel={props.rel} target={props.target}>
                    {content}
                </MuiLink>
            ) : (
                <MuiButton {...componentProps} disableRipple={true}>
                    {content}
                </MuiButton>
            )}
        </>
    );

    // The MUI tooltip requires the component to be wrapped in a HTML element
    return (
        <>
            {tooltipProps ? (
                <Tooltip title={tooltipProps.text} open={tooltipProps.show} arrow>
                    <span>{component}</span>
                </Tooltip>
            ) : (
                <>{component}</>
            )}
        </>
    );
}

export default forwardRef(Button);
